import { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Grid, MenuItem, TextField, Typography } from "@mui/material";
import { RootState, useAppDispatch } from "../../../app/features/store/store";
import RtsScanStore from "../RtsScanStore";
import { updateUserTokenToInvalid } from "../../../app/features/login/loginSlice";
import getAxiosError from "../../../app/api/axiosError";
import SelectionModal from "../../../components/common/modalPopup/selectionModal";
import { DedicatedDeliveryAgent } from "../../../app/api/dedicatedDelivery/DedicatedDeliveryAgent";
import { Client } from "../../../app/models/responseModels/clientsResponse";
import { useNavigate } from "react-router-dom";
import {
  fetchFdpDriverCredsMappingWithClientEmails,
  removeFdpDriverAccount,
  updateFdpDriverSelectedEmailValue,
} from "../../../app/features/fdp/fdpSlice";
import { FdpDriverClientAccount } from "../../../app/models/responseModels/loginResponse";
import { getFdpClients } from "../../../app/features/dedicatedDelivery/rtsSlice";

export interface RtsClientSelectionProps {
  setShowClientSelection: any;
}

const RtsClientSelection = (props: RtsClientSelectionProps) => {
  const { location, rtsUserClient, setRtsUserClient, setAssocClientId } =
    RtsScanStore();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isFdp = useSelector(
    (state: RootState) => state.login?.authResponse?.isFdp
  );
  const userFdpId = useSelector(
    (state: RootState) => state.login?.authResponse?.fdpDetails?.id
  );
  const { isFdpLoading, globallySelectedDriverClientEmail } = useSelector(
    (state: RootState) => state.fdp
  );
  const { fdpAssociatedClients } = useSelector((state: RootState) => state.rts);
  // states
  const [isClientSlectionModalOpen, setClientSelectionModalOpen] =
    useState(true);
  const [fdpLinkedClients, setfdpLinkedClients] = useState<Client[]>([]);
  const [disableSubmitOption, setDisableSubmitOption] = useState(true);
  const [selectedClient, setSelectedClient] = useState<Client | undefined>(
    rtsUserClient
  );

  // ***** Callbacks *****
  const getClientsForFdpUser = useCallback(async () => {
    try {
      const response = await dispatch(
        getFdpClients(Number(userFdpId))
      ).unwrap();
      if (!response.isSuccess) {
        toast.error(response.message!, {
          toastId: "fdpdriverClientList",
        });
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpdriverClientListException",
      });
    }
  }, [dispatch]);

  // ***** UseEffects *****
  useEffect(() => {
    let isFunctionRan = true;
    setfdpLinkedClients(fdpAssociatedClients);
    if (isFunctionRan && fdpAssociatedClients.length === 0) {
      getClientsForFdpUser();
      checkForDriverUserCredsMapping();
    }
    return () => {
      isFunctionRan = false;
      setClientSelectionModalOpen(true);
    };
  }, [getClientsForFdpUser, fdpAssociatedClients]);

  useEffect(() => {
    if (selectedClient) {
      setDisableSubmitOption(false);
    }
  }, [selectedClient]);

  // ***** handlers *****
  const handleModalClose = (isClientSelected: boolean = false) => {
    props.setShowClientSelection(false);
    setClientSelectionModalOpen(false);
    if (!isClientSelected) {
      navigate("/");
    }
  };

  const handleChange = (event: any) => {
    const selectedClient = fdpLinkedClients.find(
      (item) => item.id === event.target.value
    );
    if (selectedClient) {
      setSelectedClient(selectedClient);
    }
  };

  const selectionAsyncSubmitOperation = async () => {
    if (!isAnyStoreAvailableForSelectedClient()) {
      toast.error(
        "No associated client stores found in your geo location. Please " +
          "select a valid client",
        {
          toastId: "NoStoreAvailable",
        }
      );
      return false;
    }
    if (
      globallySelectedDriverClientEmail &&
      globallySelectedDriverClientEmail.clientId !== selectedClient?.id
    ) {
      await deleteSelectedDriverEmail();
    }
    setRtsUserClient(selectedClient);
    handleModalClose(true);
    return true;
  };

  const checkForDriverUserCredsMapping = async () => {
    try {
      await dispatch(fetchFdpDriverCredsMappingWithClientEmails()).unwrap();
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpdriverEmailRemoveException",
      });
    }
  };

  const deleteSelectedDriverEmail = async () => {
    try {
      const response = await dispatch(
        removeFdpDriverAccount(
          globallySelectedDriverClientEmail as FdpDriverClientAccount
        )
      ).unwrap();
      if (response.isSuccess) {
        dispatch(updateFdpDriverSelectedEmailValue(null));
        setAssocClientId(undefined);
        return true;
      }
    } catch (error) {
      const appError = getAxiosError(error);
      toast.error(appError.errorMessage, {
        toastId: "fdpdriverEmailRemoveException",
      });
      return false;
    }
    return true;
  };

  const isAnyStoreAvailableForSelectedClient = () => {
    const { clientId, redundantLocations } = location;
    if (selectedClient) {
      if (clientId === selectedClient.id) {
        return true;
      } else if (redundantLocations) {
        return redundantLocations.some(
          (loc: any) => loc.clientId === selectedClient.id
        );
      }
    }
    return false;
  };

  return (
    <>
      {!isFdpLoading && isFdp && (
        <SelectionModal
          onClose={handleModalClose}
          asyncOperation={selectionAsyncSubmitOperation}
          open={isClientSlectionModalOpen}
          title={"Client Selection"}
          showCancelOption={true}
          disableSubmit={disableSubmitOption}
        >
          <Grid item xs={12} sm={8} md={8} lg={8} mt={1}>
            <Typography variant="h6">Select client for today</Typography>
          </Grid>
          <Grid item xs={12} sm={8} md={8} lg={8} mt={1}>
            <TextField
              select
              fullWidth
              id="client"
              label="Client"
              size="small"
              variant="outlined"
              value={selectedClient?.id || ""}
              onChange={handleChange}
            >
              {fdpLinkedClients.map((item, index) => (
                <MenuItem key={index} value={item.id}>
                  {item.clientName}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </SelectionModal>
      )}
    </>
  );
};

export default RtsClientSelection;
